import Basket from '@westwing/ui-kit/Icons/Basket';
import * as React from 'react';
import styled from 'styled-components';
import { micro } from '@westwing/ui-kit/typography';
import { CartProductItem } from '@westwing/ui-kit/ContentFlyout/CartFlyout/types';
import { useEffect } from 'react';
import debounce from 'lodash.debounce';
import useCartTimer from '../hooks/useCartTimer';
import WWEvents, { EventEntry } from '../WWEvents';
import RequestCartInfo from '../helpers/RequestCartInfo';
import { useClubContext } from '../hooks/ClubContext';
import { ClubLink } from '../types/clubLinksTypes';
import CartFlyoutWrapper from './CartFlyoutWrapper';
import HeaderIconLink from 'Components/IconLink';
import ErrorBoundary from 'Components/ErrorBoundary';
import { useHeaderContext } from 'Components/HeaderContext';

export interface CartIconProps {
    formattedQuantity: string;
    secondsLeft: number;
    remindAt: number;
    showMinicartAt: number;
    products: CartProductItem[];
    totalPrice: string;
}

const BADGE_WRAPPER_HEIGHT = '20px';
const BadgeWrapper = styled.div`
    position: relative;
    display: inline-block;
    height: ${BADGE_WRAPPER_HEIGHT};
    width: 50%;
    text-align: center;
    line-height: normal;
`;

const BADGE_SIZE = '14px';
const Badgebubble = styled.span`
    ${micro}
    position: absolute;
    color: ${({ theme }) => theme.colors.white};
    border-radius: 50%;
    width: ${BADGE_SIZE};
    height: ${BADGE_SIZE};
    line-height: ${BADGE_SIZE};
    text-align: center;
    left: 11px;
    top: -7px;
    background-color: ${({ theme }) => theme.colors.turquoise100};
    &:empty {
        display: none;
    }
`;

const Timer = styled.time`
    ${micro}
    color: ${({ theme }) => theme.colors.charcoal50};
    display: block;
    position: absolute;
    width: 100%;
    top: ${BADGE_WRAPPER_HEIGHT};
    &:empty {
        display: none;
    }
`;

const StyledBasket = styled(Basket)`
    vertical-align: baseline;
`;

const Wrapper = styled.div`
    position: relative;
`;

const parseSeconds = (seconds: number) => {
    const date = new Date();
    date.setHours(1, 0, seconds);
    return date.toISOString().substr(14, 5);
};

const CartIcon = ({
    formattedQuantity,
    secondsLeft,
    remindAt,
    showMinicartAt,
    products = [],
    totalPrice,
}: CartIconProps): JSX.Element => {
    const [serverRemainingSeconds, setServerRemainingSeconds] = React.useState(secondsLeft);
    const [timer, setTimer] = React.useState('');
    const { startTimer, remainingTime, resetTimer } = useCartTimer(remindAt, showMinicartAt);
    const [quantity, setQuantity] = React.useState(formattedQuantity);
    const { links } = useClubContext();
    const { t } = useHeaderContext();
    const [isCartFlyoutVisible, setIsCartFlyoutVisible] = React.useState(false);
    const [miniCartFlyoutProducts, setMiniCartFlyoutProducts] = React.useState(products);
    const [miniCartSubTotal, setMiniCartSubTotal] = React.useState(totalPrice);
    const [isDeliveryUpdateVisible, setIsDeliveryUpdateVisible] = React.useState(false);
    const {
        trackingHelpers: {
            ga: { trackNonInteractiveGenericEvent },
        },
    } = useHeaderContext();

    React.useEffect(() => {
        setMiniCartSubTotal(totalPrice);
    }, [totalPrice]);

    const updateQuantity = React.useCallback((itemCount?: number) => {
        if (!itemCount) {
            setQuantity('');
            return;
        }
        setQuantity(itemCount.toString());
    }, []);

    const updateTimer = React.useCallback((seconds: number) => {
        const time = seconds === 0 ? '' : parseSeconds(seconds);
        setTimer(time);
    }, []);

    React.useEffect(() => {
        const events: EventEntry[] = [
            {
                event: 'CART_EXPIRED',
                listener: () => {
                    updateQuantity();
                    setMiniCartSubTotal('');
                },
            },
            {
                event: 'ADDED_TO_CART',
                listener: response => {
                    if (!response.expired) {
                        // control expiry styles
                    }

                    if (response.delivery_times_changed) {
                        setIsDeliveryUpdateVisible(true);
                    }

                    updateQuantity(response.total);
                    updateTimer(response.seconds_left);
                    setServerRemainingSeconds(response.seconds_left);
                    setIsCartFlyoutVisible(true);
                    setMiniCartFlyoutProducts(response.products);
                    setMiniCartSubTotal(response.totalPrice);
                    setTimeout(() => {
                        setIsCartFlyoutVisible(false);
                        setIsDeliveryUpdateVisible(false);
                    }, 7000);
                },
            },
            {
                event: 'CART_RESTORED',
                listener: timeLeft => {
                    updateTimer(timeLeft);
                    startTimer(timeLeft);
                },
            },
        ];

        events.forEach(event => WWEvents.push(event));
        return () => events.forEach(({ event, listener }) => WWEvents.remove(event, listener));
    }, [startTimer, updateQuantity, updateTimer]);

    useEffect(() => {
        const getCartDetails = async () => {
            const response = await RequestCartInfo(links[ClubLink.CART_REQUEST].path);
            if (response.success === true) {
                // eslint-disable-next-line @typescript-eslint/no-shadow,camelcase
                const { products = [], totalPrice = '', seconds_left = 0, total = 0 } = response.code;
                setServerRemainingSeconds(seconds_left);
                updateQuantity(total);

                setMiniCartSubTotal(totalPrice);
                setMiniCartFlyoutProducts(products);
            }
        };

        const update = debounce(getCartDetails, 5000, { leading: true });

        window.addEventListener('focus', update);
        return () => window.removeEventListener('focus', update);
    }, [updateTimer, updateQuantity, setMiniCartFlyoutProducts, setMiniCartSubTotal, links]);

    React.useEffect(() => {
        if (serverRemainingSeconds > 0) {
            startTimer(serverRemainingSeconds);
        } else {
            resetTimer();
        }
    }, [startTimer, resetTimer, serverRemainingSeconds]);

    React.useEffect(() => {
        updateTimer(remainingTime);
    }, [remainingTime, updateTimer]);

    React.useEffect(() => {
        updateQuantity(parseInt(formattedQuantity, 10));
    }, [formattedQuantity, updateQuantity]);

    React.useEffect(() => {
        setServerRemainingSeconds(secondsLeft);
    }, [secondsLeft]);

    React.useEffect(() => {
        setMiniCartFlyoutProducts(products);
    }, [products]);

    const onMouseEnter = React.useCallback(() => {
        setIsCartFlyoutVisible(true);

        if (miniCartFlyoutProducts && miniCartFlyoutProducts.length > 0) {
            trackNonInteractiveGenericEvent('Header', { action: 'Hover Shopping Cart' });
        }
    }, [miniCartFlyoutProducts, trackNonInteractiveGenericEvent]);

    return (
        <Wrapper>
            <HeaderIconLink
                href={links[ClubLink.CART].path}
                data-testid="oww-header-cart-button"
                aria-label={t('OwwHeader_To Cart')}
                onMouseEnter={onMouseEnter}
                onMouseLeave={() => {
                    setIsCartFlyoutVisible(false);
                    setIsDeliveryUpdateVisible(false);
                }}
                onClick={() => trackNonInteractiveGenericEvent('Header', { action: 'Click Shopping Cart' })}
            >
                <BadgeWrapper>
                    <Badgebubble data-testid="oww-header-cart-icon-bubble">{quantity}</Badgebubble>
                    <StyledBasket />
                    <Timer data-testid="oww-header-cart-icon-timer">{timer}</Timer>
                </BadgeWrapper>
            </HeaderIconLink>
            {miniCartFlyoutProducts && miniCartFlyoutProducts.length > 0 && (
                <ErrorBoundary fallbackComponent={null}>
                    <CartFlyoutWrapper
                        isVisible={isCartFlyoutVisible}
                        amountOfItems={quantity ? parseInt(quantity, 10) : 0}
                        remainingTime={remainingTime}
                        remindAt={remindAt}
                        products={miniCartFlyoutProducts}
                        totalPrice={miniCartSubTotal}
                        isDeliveryUpdateVisible={isDeliveryUpdateVisible}
                    />
                </ErrorBoundary>
            )}
        </Wrapper>
    );
};

export default CartIcon;
